<template>
  <div class="invoice-form">
    <form @submit.stop.prevent="form.handleSubmit()">
      <!-- invoice details -->
      <div class="grid grid-cols-2 gap-8">
        <div>
          <BaseInput
            :model-value="project.name"
            name="project"
            label="Project"
            :disabled="true"
          />
        </div>

        <div>
          <BaseInput
            :model-value="invoice.invoiceId"
            name="invoiceId"
            label="Invoice #"
            :disabled="true"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-8">
        <div>
          <form.Field
            name="dueDate"
            :validators="{
              onBlur: yup.string().required('Due Date is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Due date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>

        <div>
          <form.Field
            name="invoiceDate"
            :validators="{
              onBlur: yup.string().required('Invoice Date is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Invoice Date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <!-- items -->
      <h4 class="mt-6">Items</h4>
      <hr class="mt-2">

      <div>
        <form.Field
          name="items"
          :validators="{
            onBlur: yup.array().min(1, 'At least one invoice item is required')
          }"
        >
          <template #default="{ field }">
            <div
              v-if="(field.state.meta.errors && field.state.meta.errors[0]) && form.getFieldValue('items').length === 0"
              class="text-base/6 text-error sm:text-sm/6 mt-2 flex flex-col items-center"
            >
              <span class="i-heroicons-exclaimation-circle size-8" />
              <p class="text-base/6 text-error sm:text-sm/6">
                {{ field.state.meta.errors[0] }}
              </p>
            </div>
          </template>
        </form.Field>

        <div
          v-for="(item, i) in invoiceItems"
          :key="item.id"
          :class="{ 'mt-8': i !== 0, 'mt-4': i === 0 }"
        >
          <div>
            <h3>{{ item.title }}</h3>
          </div>

          <div class="mt-2">
            <span v-html="item.description" />
          </div>

          <hr class="w-3/4 mt-8 mb-4">

          <div class="grid grid-cols-2">
            <div>
              <span class="font-semibold mr-2">Quantity:</span> <span>{{ item.quantity }}</span>
            </div>

            <div>
              <span class="font-semibold mr-2">Unit Price:</span> <span>{{ currency(item.unitPrice)}}</span>
            </div>

            <div class="mt-4">
              <span class="font-semibold mr-2">Total Price:</span> <span>{{ currency(item.total) }}</span>
            </div>
          </div>

          <hr class="my-4">
        </div>
      </div>

      <!-- select items button -->
      <div class="my-8 flex justify-center items-center">
        <BaseButton
          type="button"
          style="box-shadow: none !important;"
          @click="showModal = true"
        >
          <span class="i-heroicons-plus-circle size-8" />
          <br>
          <p class="text-lg">Select invoice items</p>
        </BaseButton>
      </div>

      <!-- total, credit, balance -->
      <div class="mt-5 ml-auto min-w-[30%] max-w-fit">
        <div class="bg-slate-100 p-4 rounded-lg gap-4 text-right">
          <div class="grid grid-cols-2">
            <span class="font-semibold">Credit:</span> <span>{{ currency(form.getFieldValue('credit')) }}</span>
          </div>
          <div class="grid grid-cols-2">
            <span class="font-semibold">Balance:</span> <span>{{ currency(form.getFieldValue('balance')) }}</span>
          </div>
          <div class="grid grid-cols-2">
            <span class="font-semibold">Total:</span> <span>{{ currency(form.getFieldValue('total')) }}</span>
          </div>
        </div>
      </div>

      <!-- submit button -->
      <hr class="mt-6">
      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="{ name: 'ListInvoices' }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>

    <!-- select items modal -->
    <Modal
      v-model="showModal"
      title="Select Invoice Items"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
      width="w-full md:w-[90%] lg:w-[80%] xl:w-[60%]"
    >
      <template #body>
        <div class="grid grid-cols-3 gap-4 mt-6">
          <div class="flex flex-col items-center">
            <div class="flex items-center">
              <span class="i-heroicons-currency-dollar size-5 mr-2"/>
              <h3>Job Total Value</h3>
            </div>
            <p class="text-base mt-2">{{ currency(jobsTotalValue) }}</p>
          </div>

          <div class="flex flex-col items-center">
            <div class="flex items-center">
              <span class="i-heroicons-arrow-right-circle size-5 mr-2"/>
              <h3>Previously Invoiced</h3>
            </div>
            <p class="text-base mt-2">{{ currency(invoicedValue.previouslyInvoicedValue) }} <span v-if="invoicedValue.previouslyInvoicedCredit" class="text-error text-xs">(-{{ currency(invoicedValue.previouslyInvoicedCredit) }})</span></p>
          </div>

          <div class="flex flex-col items-center">
            <div class="flex items-center">
              <span class="i-heroicons-calculator size-5 mr-2"/>
              <h3>This Invoice</h3>
            </div>
            <p class="text-base mt-2" :class="Math.abs(thisInvoice.value) + Math.abs(invoicedValue.previouslyInvoicedValue) > jobsTotalValue ? 'text-error' : 'text-success'">
              {{ currency(thisInvoice.value) }}
            </p>
            <div class="text-xs">
              <span>{{ currency(thisInvoice.absValue - thisInvoice.credit) }}</span>/<span class="text-error">{{ currency(thisInvoice.credit) }}</span>
            </div>
          </div>
        </div>

        <!-- <div class="mt-8">
          <BaseSelect
            v-model="selectedMethod"
            :options="selectOptions"
          />
        </div> -->

        <form @submit.stop.prevent>
          <div class="mt-6">
            <selectItemsForm.Field name="items">
              <template #default>
                <BaseTable
                  :fields="selectItemsFormFields"
                  :items="selectItemsForm.getFieldValue('items')"
                >
                  <template #arrow-header>
                    <div class="text-center">
                      <span class="i-heroicons-arrow-right size-4"/>
                    </div>
                  </template>
                  <template #arrow-data>
                    <div class="text-center">
                      <span class="i-heroicons-arrow-right size-4"/>
                    </div>
                  </template>

                  <template #title-data="{ row, index }">
                    <div v-if="row.id.endsWith('-credit')">
                      <selectItemsForm.Field
                        :name="`items[${index}].title`"
                        :validators="{
                          onBlur: yup.string().required('Title is required')
                        }"
                      >
                        <template #default="{ field: subField }">
                          <BaseInput
                            :model-value="(subField.state.value as string)"
                            :name="subField.name"
                            :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                            @blur="subField.handleBlur"
                            @input="(e) => subField.handleChange(e.target.value)"
                          />
                        </template>
                      </selectItemsForm.Field>
                    </div>

                    <div v-else>
                      <span>{{ row.title }}</span>
                    </div>
                  </template>

                  <template #totalPrice-data="{ row }">
                    <div>
                      <span>{{ currency(row.total) }}</span>
                    </div>
                  </template>

                  <template #amount-data="{ row, index }">
                    <selectItemsForm.Field
                      :name="`items[${index}].amount`"
                      :validators="{
                        onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number', getErrorMessage(row), (value) => isValidAmount(value, row.total, row))
                      }"
                    >
                      <template #default="{ field: subField }">
                        <BaseInputCurrency
                          :model-value="(subField.state.value as string)"
                          :name="subField.name"
                          :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                          @blur="subField.handleBlur"
                          @input="(e) => {setAmount(index, e.target.value); subField.handleChange(e.target.value)}"
                        />
                      </template>
                    </selectItemsForm.Field>
                  </template>
                </BaseTable>
              </template>
            </selectItemsForm.Field>
          </div>
        </form>
      </template>

      <template #footer>
        <hr class="mx-4 sm:mx-6">
        <selectItemsForm.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <div class="flex justify-end gap-2 my-3 mr-4 sm:mr-6">
              <BaseButton
                variant="base"
                text="Cancel"
                :disabled="isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
                @click="showModal = false"
              />

              <BaseButton
                variant="primary"
                text="Confirm"
                :disabled="(Math.abs(thisInvoice.value) + Math.abs(invoicedValue.previouslyInvoicedValue) > jobsTotalValue) || isSubmitting || !canSubmit"
                :class="{ 'cursor-not-allowed opacity-50': (Math.abs(thisInvoice.value) + Math.abs(invoicedValue.previouslyInvoicedValue) > jobsTotalValue) || isSubmitting || !canSubmit }"
                @click="selectItemsForm.handleSubmit()"
              />
            </div>
          </template>
        </selectItemsForm.Subscribe>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import { Invoice, InvoiceItem } from '@/models/invoice'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { computed, onMounted, PropType, ref, watch } from 'vue'
import { BudgetItem } from '@/models/budget'
import { Project } from '@/models/project'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import { currency, toNumber } from '@/utils'
import Modal from '@/components/app/AppModal.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import InvoiceService from '@/services/InvoiceService'
import { useQuery } from '@tanstack/vue-query'


const props = defineProps({
  model: {
    type: Object as PropType<Invoice>,
    required: true
  },
  budgetLineItems: {
    type: Array as PropType<BudgetItem[]>,
    required: true
  },
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  saveFunction: {
    type: Function,
    required: true
  }
})


const invoice = ref({ ...props.model })
const showModal = ref(false)
const invoiceItems = ref([])
const jobsTotalValue = props.budgetLineItems.reduce((acc, item) => acc + item.totalPrice, 0)
const thisInvoice = ref({
  value: invoice.value.balance,
  absValue: invoice.value.items.reduce((acc, item) => acc + Math.abs(item.total), 0),
  credit: invoice.value.credit
})

const invoiceService = new InvoiceService()
const { data:invoicedValue, isLoading: isInvoicedValueLoading } = useQuery({
  queryKey: ['invoicedValue', invoice.value.projectId],
  queryFn: async () =>  await invoiceService.getPreviouslyInvoicedValue(invoice.value.projectId, invoice.value.id),
})

const addSelectedItems = (items: InvoiceItem[]) => {
  invoiceItems.value = []
  form.setFieldValue('items', [])
  form.setFieldValue('total', 0)
  form.setFieldValue('balance', 0)
  form.setFieldValue('credit', 0)

  items.forEach(item => {
    let lineItem = props.budgetLineItems.find(budgetItem => budgetItem.id === item.id)

    if (lineItem || item.id.endsWith('-credit')) {
      form.pushFieldValue('items', item)
      invoiceItems.value.push({ ...item, title: lineItem ? lineItem.title : item.title })

      const amount = toNumber(item.total)

      if (amount < 0) {
        form.setFieldValue('credit', Math.abs(toNumber(form.getFieldValue('credit'))) + Math.abs(amount))
      } else {
        form.setFieldValue('total', toNumber(form.getFieldValue('total')) + Math.abs(amount))
      }
      form.setFieldValue('balance', toNumber(form.getFieldValue('total')) - toNumber(form.getFieldValue('credit')))
    }
  })
  form.validateField('items', 'blur')
}

onMounted(() => {
  addSelectedItems(props.model.items)
})


const form = useForm({
  defaultValues: invoice.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const updatedInvoice = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as Invoice
    updatedInvoice.dueDate = new Date(updatedInvoice.dueDate)
    updatedInvoice.invoiceDate = new Date(updatedInvoice.invoiceDate)
    await props.saveFunction(updatedInvoice)
  }
})

const selectItemsFormFields = ref([
  { key: 'title', label: 'Title' },
  { key: 'totalPrice', label: 'Contract Value' },
  { key: 'arrow', label: '' },
  { key: 'amount', label: '$ to be invoiced now' },
])

const selectItemsFormItems = ref([] as { id: string, title: string, scopeOfWork: string, total: number, amount: number }[])

props.budgetLineItems.forEach(item => {
  if (item.selected) {
    const invoiceItem = props.model.items.find(invoiceItem => invoiceItem.id === item.id)
    const amount = invoiceItem ? invoiceItem.total : 0

    selectItemsFormItems.value.push({
      id: item.id,
      title: item.title,
      scopeOfWork: item.scopeOfWork,
      total: item.totalPrice,
      amount
    })
  }
})

props.model.items.forEach(item => {
  if (item.id.endsWith('-credit')) {
    selectItemsFormItems.value.push({
      id: item.id,
      title: item.title,
      scopeOfWork: item.description,
      total: item.total,
      amount: item.total
    })
  }
})

watch(() => isInvoicedValueLoading.value, (newVal) => {
  const alreadyHasCredit = invoice.value?.credit && invoice.value.credit > 0
  if (!newVal && invoicedValue.value && !alreadyHasCredit) {
    selectItemsFormItems.value.push({
      id: `${new Date().getTime()}-credit`,
      title: 'Credit',
      scopeOfWork: 'Credit for this invoice',
      total: jobsTotalValue - Math.abs(invoicedValue.value.previouslyInvoicedValue),
      amount: 0
    })
  }
}, { immediate: true })

const selectItemsForm = useForm({
  defaultValues: {
    items: selectItemsFormItems.value
  },
  validatorAdapter: yupValidator(),
  onSubmit: ({ value }) => {
    const items = value.items
    const selectedItems = [] as InvoiceItem[]

    items.forEach(item => {
      if (!isNaN(toNumber(item.amount)) && toNumber(item.amount) !== 0) {
        const isCreditItem = item.id.endsWith('-credit')
        const description = isCreditItem ? `<p><b>Credit for this invoice  ${currency(toNumber(item.amount))}</b></p>` : `${item.scopeOfWork}<br><br> <p><b>Amount to invoice for this work ${currency(toNumber(item.amount))}</b></p>`

        selectedItems.push({
          id: item.id,
          title: item.title,
          total: toNumber(item.amount),
          description: description,
          quantity: 1,
          unitPrice: isCreditItem ? toNumber(item.amount) : toNumber(item.total),
        })
      }
    })

    addSelectedItems(selectedItems)
    showModal.value = false
  }
})

const isValidAmount = (value: string, total: number, item) => {
  if(!value) return true

  if (!item.id.endsWith('-credit')) {
    return !isNaN(toNumber(value)) && toNumber(value) >= -Math.abs(total) && toNumber(value) <= Math.abs(total)
  } else {
    const totalBalance = jobsTotalValue - Math.abs(invoicedValue.value.previouslyInvoicedValue)
    return !isNaN(toNumber(value)) && toNumber(value) >= -Math.abs(totalBalance) && toNumber(value) <= Math.abs(totalBalance)
  }
}

const setAmount = (index: number, value) => {
  selectItemsForm.useStore((state) => state.values.items[index].amount = value)
  calculateThisInvoiceAmount()
}

const calculateThisInvoiceAmount = () => {
  const items = selectItemsForm.getFieldValue('items') || []
  thisInvoice.value.value = 0
  thisInvoice.value.absValue = 0
  thisInvoice.value.credit = 0

  items.forEach(item => {
    thisInvoice.value.value += toNumber(item.amount)
    thisInvoice.value.absValue += Math.abs(toNumber(item.amount))
    thisInvoice.value.credit += toNumber(item.amount) < 0 ? Math.abs(toNumber(item.amount)) : 0
  })
}

const getErrorMessage = computed(() => (item) => {
  if (item.id.endsWith('-credit')) {
    const totalBalance = jobsTotalValue - Math.abs(invoicedValue.value.previouslyInvoicedValue)
    return `Amount must be a valid number, between -${currency(Math.abs(totalBalance))} and ${currency(Math.abs(totalBalance))}`
  } else {
    return `Amount must be a valid number, between -${currency(Math.abs(item.total))} and ${currency(Math.abs(item.total))}`
  }
})

</script>
